import { Box, Card, CardHeader, Dialog, IconButton, Stack, TextField, Tooltip, Button as MuiButton } from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../router'
import { ClienteTipoLCTCDTO, ClienteTipoLCTCFields, ClienteSubTipoLCTCDTO, ClienteSubTipoLCTCFields } from './types'
import { HttpClienteTipoLCTC, HttpClienteSubTipoLCTC } from './api'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import EditClienteTipoLCTCForm from './EditClienteTipoForm'
import EditClienteSubTipoLCTCForm from './EditClienteSubTipoForm'
import { ItecColFilters, ItecDataGrid, ItecGridColsDef as GridColsDef, useItecDataGrid } from 'src/lib/itaptec/datagrid/src'
import CachedIcon from '@mui/icons-material/Cached'
import { FilterAltOff, Search } from '@mui/icons-material'
import { WrapperTable } from '../components'
import { EntityWithIdFields, schemaSelectionOption } from 'src/types'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { Button } from 'src/components/forms'
import { ICON_MAS } from '../../../../sistema/components/SharedIcons'

const schemaFiltroTipo = z.object({ nombre: z.string().optional() })
type SchemaFiltroTipo = z.infer<typeof schemaFiltroTipo>

function TableClienteTipoLCTC() {
    const { askConfirmation } = useConfirmationDialog()
    const navigate = useNavigate()

    // --- Tipo de Cliente ---
    const [selectedTipoEdit, setSelectedTipoEdit] = useState<ClienteTipoLCTCDTO>()
    const [showTipoDialog, setShowTipoDialog] = useState(false)

    const columnasTipo: GridColsDef<ClienteTipoLCTCDTO>[] = [
        { headerName: 'Tipo de Cliente', field: ClienteTipoLCTCFields.Descripcion, flex: 3 },
        {
            field: 'actions', type: 'actions',
            getActions: ({ row }) => [
                <GridActionsCellItem key={1}
                    icon={<Tooltip title='Editar'><EditIcon color='info' /></Tooltip>}
                    label='Editar' showInMenu
                    onClick={() => { setSelectedTipoEdit(row); setShowTipoDialog(true) }}
                />,
                <GridActionsCellItem key={2}
                    icon={<Tooltip title='Inactivar'><PersonRemoveIcon color='error' /></Tooltip>}
                    label='Inactivar' showInMenu
                    onClick={() => askConfirmation(() => {
                        toast.promise(HttpClienteTipoLCTC.deactivate(row[EntityWithIdFields.Id]), {
                            success: 'Inactivado', error: 'Error', loading: 'Inactivando'
                        }).then(() => refetchTipo())
                    })}
                />
            ]
        }
    ]

    const filtrosTipo: ItecColFilters<SchemaFiltroTipo> = {
        [ClienteTipoLCTCFields.Descripcion]: { type: 'text', name: 'nombre', props: { debounceTime: 500, size: 'small' } }
    }

    const {
        datagridProps: datagridPropsTipo,
        control: { query: { refetch: refetchTipo }, form: filterFormTipo }
    } = useItecDataGrid<ClienteTipoLCTCDTO, SchemaFiltroTipo>({
        pagination: false, columns: columnasTipo, filters: filtrosTipo,
        getEntities: (filter) => HttpClienteTipoLCTC.getAll().then(data => {
            if (!filter?.nombre) return data
            const lower = filter.nombre.toLowerCase()
            return data.filter(s => s[ClienteTipoLCTCFields.Descripcion]?.toLowerCase().includes(lower))
        }),
        density: 'compact'
    })

    const cleanTipoProps = {
        ...datagridPropsTipo,
        columns: datagridPropsTipo.columns.map(col => ({ ...col, disableColumnMenu: true, renderHeader: undefined })),
        slots: undefined
    }

    // --- Subtipo de Cliente ---
    const [selectedSubTipoEdit, setSelectedSubTipoEdit] = useState<ClienteSubTipoLCTCDTO>()
    const [showSubTipoDialog, setShowSubTipoDialog] = useState(false)
    const [subtipos, setSubtipos] = useState<ClienteSubTipoLCTCDTO[]>([])
    const [subtipoFilter, setSubtipoFilter] = useState('')
    const [loadingSubtipos, setLoadingSubtipos] = useState(false)

    const fetchSubtipos = () => {
        setLoadingSubtipos(true)
        HttpClienteSubTipoLCTC.getAll()
            .then(setSubtipos)
            .finally(() => setLoadingSubtipos(false))
    }

    useEffect(() => { fetchSubtipos() }, [])

    const filteredSubtipos = subtipoFilter
        ? subtipos.filter(s => s[ClienteSubTipoLCTCFields.Descripcion]?.toLowerCase().includes(subtipoFilter.toLowerCase()))
        : subtipos

    const columnasSubTipo: GridColDef<ClienteSubTipoLCTCDTO>[] = [
        { headerName: 'Subtipo de Cliente', field: ClienteSubTipoLCTCFields.Descripcion, flex: 3 },
        {
            field: 'actions', type: 'actions',
            getActions: ({ row }) => [
                <GridActionsCellItem key={1}
                    icon={<Tooltip title='Editar'><EditIcon color='info' /></Tooltip>}
                    label='Editar' showInMenu
                    onClick={() => { setSelectedSubTipoEdit(row); setShowSubTipoDialog(true) }}
                />,
                <GridActionsCellItem key={2}
                    icon={<Tooltip title='Inactivar'><PersonRemoveIcon color='error' /></Tooltip>}
                    label='Inactivar' showInMenu
                    onClick={() => askConfirmation(() => {
                        toast.promise(HttpClienteSubTipoLCTC.deactivate(row[EntityWithIdFields.Id]), {
                            success: 'Inactivado', error: 'Error', loading: 'Inactivando'
                        }).then(() => fetchSubtipos())
                    })}
                />
            ]
        }
    ]

    return (
        <WrapperTable>
            <Stack spacing={3}>
                {/* Botón de relaciones */}
                <Box>
                    <MuiButton
                        variant='outlined'
                        color='primary'
                        startIcon={<LinkIcon />}
                        onClick={() => navigate(`/admin/${ROUTES.CLIENTE_TIPO_LCTC_RELACIONES}`)}
                    >
                        Configurar Relaciones (Sector - Tipo - Subtipo)
                    </MuiButton>
                </Box>

                {/* Tipo de Cliente */}
                <Card>
                    <CardHeader
                        title='Tipos de Cliente - LCTC'
                        action={
                            <Stack direction='row'>
                                <IconButton disabled={datagridPropsTipo.loading} onClick={() => refetchTipo()}>
                                    <CachedIcon />
                                </IconButton>
                                <Button variant='contained' color='primary' endIcon={ICON_MAS}
                                    onClick={() => { setSelectedTipoEdit(undefined); setShowTipoDialog(true) }}>
                                    Nuevo
                                </Button>
                            </Stack>
                        }
                    />
                    {filterFormTipo && (
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                            <Controller control={filterFormTipo.control} name='nombre'
                                render={({ field }) => (
                                    <TextField {...field} value={field.value ?? ''} label='Buscar' size='small'
                                        sx={{ minWidth: 200, flex: 1 }}
                                        InputProps={{ startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                                    />
                                )}
                            />
                            <Tooltip title='Limpiar filtros'>
                                <IconButton size='small' onClick={() => filterFormTipo.reset({ nombre: undefined })}>
                                    <FilterAltOff fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <ItecDataGrid {...cleanTipoProps} />
                </Card>

                {/* Subtipo de Cliente */}
                <Card>
                    <CardHeader
                        title='Subtipos de Cliente - LCTC'
                        action={
                            <Stack direction='row'>
                                <IconButton disabled={loadingSubtipos} onClick={fetchSubtipos}>
                                    <CachedIcon />
                                </IconButton>
                                <Button variant='contained' color='primary' endIcon={ICON_MAS}
                                    onClick={() => { setSelectedSubTipoEdit(undefined); setShowSubTipoDialog(true) }}>
                                    Nuevo
                                </Button>
                            </Stack>
                        }
                    />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                        <TextField
                            value={subtipoFilter}
                            onChange={e => setSubtipoFilter(e.target.value)}
                            label='Buscar' size='small'
                            sx={{ minWidth: 200, flex: 1 }}
                            InputProps={{ startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
                        />
                        <Tooltip title='Limpiar filtros'>
                            <IconButton size='small' onClick={() => setSubtipoFilter('')}>
                                <FilterAltOff fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <DataGrid
                        disableColumnFilter
                        disableColumnMenu
                        columns={columnasSubTipo}
                        rows={filteredSubtipos}
                        loading={loadingSubtipos}
                        density='compact'
                        hideFooterPagination
                    />
                </Card>
            </Stack>

            {/* Dialogs */}
            <Dialog open={showTipoDialog} fullWidth maxWidth='sm' keepMounted={false}>
                <EditClienteTipoLCTCForm
                    item={selectedTipoEdit}
                    onClose={() => { setShowTipoDialog(false); setSelectedTipoEdit(undefined); refetchTipo() }}
                />
            </Dialog>
            <Dialog open={showSubTipoDialog} fullWidth maxWidth='sm' keepMounted={false}>
                <EditClienteSubTipoLCTCForm
                    item={selectedSubTipoEdit}
                    onClose={() => { setShowSubTipoDialog(false); setSelectedSubTipoEdit(undefined); fetchSubtipos() }}
                />
            </Dialog>
        </WrapperTable>
    )
}

export default TableClienteTipoLCTC
